"use client";

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

interface MarketShareChartProps {
	data: Array<{
		name: string;
		value: number;
		percentage: number;
	}>;
	isLoading?: boolean;
}

const COLORS = [
	"#3b82f6", // blue
	"#ef4444", // red
	"#10b981", // green
	"#f59e0b", // amber
	"#8b5cf6", // purple
	"#06b6d4", // cyan
	"#ec4899", // pink
	"#14b8a6", // teal
	"#f97316", // orange
	"#6366f1", // indigo
];

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{
		name: string;
		value: number;
		payload: { percentage: number };
	}>;
}) => {
	if (active && payload?.[0]) {
		const { name, value, payload: data } = payload[0];
		return (
			<div className="rounded-lg border bg-popover text-popover-foreground p-3 shadow-sm">
				<p className="font-semibold">{name}</p>
				<p className="text-sm text-muted-foreground">
					{value.toLocaleString()} requests ({data.percentage}%)
				</p>
			</div>
		);
	}
	return null;
};

export function MarketShareChart({ data, isLoading }: MarketShareChartProps) {
	if (isLoading) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">Loading chart...</div>
			</div>
		);
	}

	if (!data || data.length === 0) {
		return (
			<div className="flex h-[350px] items-center justify-center">
				<div className="text-sm text-muted-foreground">No data available</div>
			</div>
		);
	}

	return (
		<ResponsiveContainer width="100%" height={350}>
			<PieChart>
				<Pie
					data={data}
					cx="50%"
					cy="50%"
					innerRadius={60}
					outerRadius={100}
					paddingAngle={2}
					dataKey="value"
				>
					{data.map((entry, index) => (
						<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
					))}
				</Pie>
				<Tooltip content={<CustomTooltip />} />
			</PieChart>
		</ResponsiveContainer>
	);
}
